import { Box, Typography, useMediaQuery } from '@mui/material';
import {
  RichTreeView,
  TreeItem2Content,
  TreeItem2Label,
  TreeItem2SlotProps,
  TreeViewBaseItem,
  UseTreeItem2ContentSlotOwnProps,
  UseTreeItem2Parameters,
  useTreeItem2
} from '@mui/x-tree-view';
import { TreeItem2 } from '@mui/x-tree-view/TreeItem2';

import React, { ReactNode } from 'react';
import styled from 'styled-components';

import theme from 'styles/theme/theme';

interface CustomTreeItemProps
  extends Omit<UseTreeItem2Parameters, 'rootRef'>,
    Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {}

interface SettingsTreeViewProps {
  activeView: string;
  setactiveView: Function;
  items: TreeViewBaseItem[];
  expandedItems?: string[];
}

interface CustomLabelProps {
  treeItem: CustomTreeItemProps;
  children: ReactNode;
  icon: JSX.Element;
}

export const SettingsTreeView = ({
  activeView,
  setactiveView,
  items,
  expandedItems
}: SettingsTreeViewProps) => {
  const [selectedItem, setSelectedItem] = React.useState<string>(activeView || items[0].id);
  const biggerThanXS = useMediaQuery(theme('de').breakpoints.up('md'));

  const CustomTreeItemContent = styled(TreeItem2Content)(() => ({
    flexDirection: 'row-reverse',
    padding: '0em 1em',
    minHeight: '3em',
    justifyContent: biggerThanXS ? 'flex-start' : 'center',
    border: '0.5px solid #ddd',
    color: '#555',
    borderRadius: '3px'
  }));

  const StyledTreeItemLabelText = styled(Typography)({
    color: 'inherit'
  }) as unknown as typeof Typography;

  function CustomLabel({ children, icon, treeItem, ...other }: CustomLabelProps) {
    return (
      <TreeItem2Label
        {...other}
        sx={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <div style={{ marginRight: 8 }}>{icon}</div>
        <StyledTreeItemLabelText variant="body2">{children}</StyledTreeItemLabelText>
      </TreeItem2Label>
    );
  }

  const CustomTreeItem = React.forwardRef(function CustomTreeItem(
    props: CustomTreeItemProps,
    ref: React.Ref<HTMLLIElement>
  ) {
    const { id, itemId, label, disabled, children } = props;

    const { getLabelProps, status, publicAPI } = useTreeItem2({
      id,
      itemId,
      children,
      label,
      disabled,
      rootRef: ref
    });

    const item = publicAPI.getItem(itemId);
    const isExpanded = status.expanded;

    const handleContentClick: UseTreeItem2ContentSlotOwnProps['onClick'] = () => {
      if (!item.children && item.id) {
        setactiveView(item.id);
        setSelectedItem(item.id);
        return;
      }

      if (isExpanded) {
        return;
      }
      if (item.children) {
        setactiveView(item.children[0].id);
        setSelectedItem(item.children[0].id);
      }
    };

    return (
      <TreeItem2
        style={{ borderTopLeftRadius: 12 }}
        {...props}
        ref={ref}
        slots={{
          content: CustomTreeItemContent,
          label: CustomLabel
        }}
        slotProps={
          {
            content: { onClick: handleContentClick },
            label: {
              ...getLabelProps({
                icon: item.icon,
                treeItem: item
              })
            }
          } as TreeItem2SlotProps
        }
      />
    );
  });

  return (
    <Box sx={{ minHeight: 200, minWidth: 250 }}>
      <RichTreeView
        defaultExpandedItems={expandedItems}
        items={items}
        slots={{ item: CustomTreeItem }}
        selectedItems={selectedItem}
      />
    </Box>
  );
};
